/**
 * vr_draw — the canvas vocabulary every in-headset surface shares (XR P2 "VR UI / UX", 2026-09-28): the theme's
 * colours and faces read through `themeColor` (THE TOKEN LAW — the VR panels change with Options → General →
 * Interface theme like every DOM window), rounded slabs, icons (the shared set + the few canvas-only ones), bars,
 * and text that fits its box. No DOM, no three.
 */

import { themeColor, type ThemeToken } from '../../lib/ui_theme.js';
import { ICON_PATHS } from '../../lib/icons.js';
import { EXTRA_ICON_PATHS, type Rect } from '../../lib/xr_ui_layout.js';

export type Tok = Exclude<ThemeToken, '--ax-font' | '--ax-display' | '--ax-mono' | '--ax-radius' | '--ax-win-radius' | '--ax-win-bg' | '--ax-win-border' | '--ax-win-frame' | '--ax-win-blur' | '--ax-win-outline' | '--ax-seam-left' | '--ax-seam-top' | '--ax-bar-bg' | '--ax-grain' | '--ax-scrim' | '--ax-shadow'>;

/** A theme colour (hex / rgb) with a fallback for a page with no theme sheet (a harness). */
export function col(t: Tok, fallback = '#cccccc'): string {
  const v = themeColor(t, fallback).trim();
  return v || fallback;
}

/** A theme colour at an alpha (canvas takes color-mix poorly; hex → rgba). */
export function colA(t: Tok, a: number, fallback = '#cccccc'): string {
  return withAlpha(col(t, fallback), a);
}

export function withAlpha(c: string, a: number): string {
  const s = c.trim();
  let m = /^#([0-9a-f]{3})$/i.exec(s);
  if (m) {
    const h = m[1];
    return `rgba(${parseInt(h[0] + h[0], 16)},${parseInt(h[1] + h[1], 16)},${parseInt(h[2] + h[2], 16)},${a})`;
  }
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s);
  if (m) {
    const h = m[1];
    return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
  }
  const r = /^rgba?\(([^)]+)\)$/i.exec(s);
  if (r) {
    const parts = r[1].split(',').map((x) => x.trim());
    return `rgba(${parts[0]},${parts[1]},${parts[2]},${a})`;
  }
  return s;
}

/** A canvas font string from the theme's faces. */
export function font(px: number, face: 'body' | 'display' | 'mono' = 'body', weight = 400): string {
  const fam = face === 'display' ? themeColor('--ax-display', 'system-ui') : face === 'mono' ? themeColor('--ax-mono', 'ui-monospace, monospace') : themeColor('--ax-font', 'system-ui, sans-serif');
  return `${weight} ${px}px ${fam || 'system-ui, sans-serif'}`;
}

export function slab(g: CanvasRenderingContext2D, r: Rect, radius: number, fill: string | null, stroke: string | null = null, lw = 2): void {
  g.beginPath();
  g.roundRect(r.x, r.y, r.w, r.h, radius);
  if (fill) { g.fillStyle = fill; g.fill(); }
  if (stroke) { g.strokeStyle = stroke; g.lineWidth = lw; g.stroke(); }
}

/** An icon from the shared 24-unit stroke set, or the canvas-only extras (chat, talk, comfort). */
export function icon(g: CanvasRenderingContext2D, name: string, x: number, y: number, size: number, color: string): void {
  const paths = ICON_PATHS[name] ?? EXTRA_ICON_PATHS[name] ?? ICON_PATHS.help;
  g.save();
  g.translate(x, y);
  g.scale(size / 24, size / 24);
  g.strokeStyle = color;
  g.lineWidth = 1.9;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const d of paths) g.stroke(new Path2D(d));
  g.restore();
}

/** Text clipped with an ellipsis to `maxW`. */
export function fitText(g: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (g.measureText(text).width <= maxW) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (g.measureText(text.slice(0, mid) + '…').width <= maxW) lo = mid; else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

export function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, color: string, f: string, maxW?: number, align: CanvasTextAlign = 'left'): void {
  g.font = f;
  g.fillStyle = color;
  g.textAlign = align;
  g.textBaseline = 'alphabetic';
  g.fillText(maxW ? fitText(g, s, maxW) : s, x, y);
  g.textAlign = 'left';
}

/** A horizontal meter: a track and a fill of `frac` (0..1). */
export function bar(g: CanvasRenderingContext2D, r: Rect, frac: number, fill: string, track: string): void {
  slab(g, r, r.h / 2, track);
  const f = Math.max(0, Math.min(1, Number.isFinite(frac) ? frac : 0));
  if (f > 0) slab(g, { x: r.x, y: r.y, w: Math.max(r.h, r.w * f), h: r.h }, r.h / 2, fill);
}

/** A button: a slab with a label centred; `hover` lifts it, `primary` paints it in the accent. */
export function button(g: CanvasRenderingContext2D, r: Rect, label: string, hover: boolean, primary = false, px = 22, disabled = false): void {
  const fill = primary ? col('--ax-accent', '#d9a441') : hover ? col('--ax-sel', '#3b2c1f') : col('--ax-bg3', '#2a211a');
  slab(g, r, 8, disabled ? colA('--ax-bg3', 0.5, '#2a211a') : fill, hover ? col('--ax-hi', '#ffffff') : col('--ax-line2', '#6b5a48'), hover ? 3 : 2);
  const c = disabled ? col('--ax-muted', '#8c7a66') : primary ? col('--ax-on-accent', '#120d0a') : col('--ax-text', '#efe6d2');
  text(g, label, r.x + r.w / 2, r.y + r.h / 2 + px * 0.36, c, font(px, 'body', 600), r.w - 12, 'center');
}

export type Level = 'good' | 'warn' | 'bad' | 'info';
export function levelColor(l: Level): string {
  return l === 'good' ? col('--ax-good', '#7fc97f') : l === 'warn' ? col('--ax-warn', '#e6b95a') : l === 'bad' ? col('--ax-bad', '#e0604a') : col('--ax-info', '#6db3f2');
}
